import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import {
    CrossClientDashboardData,
    AchievementFilter,
    PeriodType,
    ComparisonType
} from '../../../interfaces/dashboards/CrossClientDashboard.interface';
import CrossClientDashboardService from '../../../services/dashboards/CrossClientDashboard.service';
import ClientService from '../../../services/ClientService';
import DashboardControls from '../../../components/dashboard/cross-client/DashboardControls.component';
import ClientCard from '../../../components/dashboard/cross-client/ClientCard.component';
import { trackEventFromObject, TrackableEvent } from '../../../services/AmplitudeService';
import CurrentClientProvider from '../../../providers/CurrentClientProvider';
import { buildV3ClientDashboardPath } from '../../../services/ClientDashboardV3';

// NOTE: This is the dedicated v3-only Cross-Client Dashboard. It must never
// reference any legacy routes. It is intentionally a standalone duplicate of
// the legacy component (src/pages/dashboards/CrossClientDashboard.tsx) rather
// than a shared/branching component, so the legacy experience can never leak
// into /v3/ and vice versa.
const AmplitudeEventPageName = 'Cross-Client Dashboard';

const CrossClientDashboardV3: React.FC = () => {
    // State
    const [dashboardData, setDashboardData] = useState<CrossClientDashboardData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Filter state
    const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
    const [achievementFilter, setAchievementFilter] = useState<AchievementFilter>('all');
    const [periodType, setPeriodType] = useState<PeriodType>('current');
    const [comparisonType, setComparisonType] = useState<ComparisonType>('plan');

    const [openingClientId, setOpeningClientId] = useState<number | null>(null);

    const currentClient = CurrentClientProvider.useClient() as any;
    const navigate = useNavigate();

    const openClientDashboard = async (clientId: number, shouldTrack: boolean = true) => {
        const selectedClient = dashboardData?.clients.find(c => c.clientId === clientId);
        if (!selectedClient) {
            return;
        }

        if (shouldTrack) {
            const event = new TrackableEvent({
                pageName: AmplitudeEventPageName,
                clientCode: selectedClient.clientCode || '',
                action: 'View Client Progress',
                additionalMetadata: {
                    clientId,
                    clientName: selectedClient.clientName,
                }
            });
            trackEventFromObject(event);
        }

        try {
            setOpeningClientId(clientId);
            const searchableClients = await ClientService.searchClients('', true);
            const authoritativeClient = Array.isArray(searchableClients)
                ? searchableClients.find((client: any) => Number(client?.Id) === Number(clientId))
                : null;

            const nextClient = authoritativeClient || {
                Id: selectedClient.clientId,
                Name: selectedClient.clientName,
                ClientCode: selectedClient.clientCode || '',
            };

            currentClient?.setClient?.(nextClient);

            const clientCode = nextClient.ClientCode || selectedClient.clientCode || '';

            navigate(buildV3ClientDashboardPath(clientCode));
        } catch (err) {
            console.error('Failed to open client dashboard:', err);
            setError('Failed to open the selected client dashboard. Please try again.');
        } finally {
            setOpeningClientId(null);
        }
    };

    // Load dashboard data
    useEffect(() => {
        loadDashboardData();
    }, []);

    // Track initial visit
    useEffect(() => {
        if (!loading && dashboardData) {
            const initialVisitEvent = new TrackableEvent({
                pageName: AmplitudeEventPageName,
                clientCode: currentClient?.client?.ClientCode || '',
                action: 'Initial Visit',
                additionalMetadata: {
                    totalClients: dashboardData.clients.length
                }
            });
            trackEventFromObject(initialVisitEvent);
        }
    }, [loading, dashboardData]);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await CrossClientDashboardService.getDashboardData();
            setDashboardData(data);

            // Auto-redirect single-client users to the appropriate client dashboard
            if (data && data.clients.length === 1) {
                await openClientDashboard(data.clients[0].clientId, false);
            }
        } catch (err) {
            console.error('Failed to load dashboard data:', err);
            setError('Failed to load dashboard data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Track filter changes
    const trackFilterChange = (filterType: string, value: string) => {
        const event = new TrackableEvent({
            pageName: AmplitudeEventPageName,
            clientCode: currentClient?.client?.ClientCode || '',
            action: 'Filter Change',
            additionalMetadata: {
                filterType,
                value
            }
        });
        trackEventFromObject(event);
    };

    // Handler functions
    const handleClientSelectionChange = (clientIds: number[]) => {
        setSelectedClientIds(clientIds);
        trackFilterChange('clientSelection', `${clientIds.length} clients`);
    };

    const handleAchievementFilterChange = (filter: AchievementFilter) => {
        setAchievementFilter(filter);
        trackFilterChange('achievement', filter);
    };

    const handlePeriodTypeChange = (period: PeriodType) => {
        setPeriodType(period);
        trackFilterChange('period', period);
    };

    const handleComparisonTypeChange = (comparison: ComparisonType) => {
        setComparisonType(comparison);
        trackFilterChange('comparison', comparison);
    };

    const handleViewClientProgress = async (clientId: number) => {
        await openClientDashboard(clientId, true);
    };

    // Filtered and sorted clients
    const filteredClients = useMemo(() => {
        if (!dashboardData) return [];

        let result = [...dashboardData.clients];

        // Apply client selection filter
        result = CrossClientDashboardService.filterClientsBySelection(result, selectedClientIds);

        // Apply achievement filter
        result = CrossClientDashboardService.filterClientsByAchievement(
            result,
            achievementFilter,
            periodType,
            comparisonType
        );


        // Sort by client code
        result = CrossClientDashboardService.sortClientsByCode(result);

        return result;
    }, [dashboardData, selectedClientIds, achievementFilter, periodType, comparisonType]);

    // Loading state
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">Loading dashboard data...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen bg-gray-100">
                <div className="container mx-auto px-4 py-6 max-w-3xl">
                    <div className="bg-white rounded-lg border border-red-200 p-6">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Dashboard</h3>
                                <p className="text-red-700">{error}</p>
                            </div>
                        </div>
                        <button
                            onClick={loadDashboardData}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Empty state
    if (!dashboardData || dashboardData.clients.length === 0) {
        return (
            <div className="min-h-screen bg-gray-100">
                <div className="container mx-auto px-4 py-6 max-w-3xl">
                    <h1 className="text-3xl font-bold text-gray-900 mb-6">Cross-Client Dashboard</h1>
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Clients Found</h3>
                                <p className="text-gray-600">
                                    No clients are currently available. Please contact your administrator if you believe this is an error.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <div className="container mx-auto px-6 py-4 max-w-full">
                {/* Controls - Above Title */}
                <DashboardControls
                    clients={dashboardData.clients}
                    selectedClientIds={selectedClientIds}
                    achievementFilter={achievementFilter}
                    periodType={periodType}
                    comparisonType={comparisonType}
                    onClientSelectionChange={handleClientSelectionChange}
                    onAchievementFilterChange={handleAchievementFilterChange}
                    onPeriodTypeChange={handlePeriodTypeChange}
                    onComparisonTypeChange={handleComparisonTypeChange}
                />

                {/* Header - Below Controls */}
                <div className="mb-4 mt-2">
                    <h1 className="text-2xl font-bold text-gray-900">Cross-Client Dashboard</h1>
                    <p className="text-sm text-gray-600 mt-1">
                        Monitor sales, inflow, and inventory across all clients - {periodType === 'current' ? 'Current Month' : 'Last Month'} vs {comparisonType === 'plan' ? 'Plan' : 'Last Year'}
                    </p>
                </div>

                {/* Client Cards */}
                {filteredClients.length > 0 ? (
                    <div className="space-y-3">
                        {filteredClients.map(client => (
                            <ClientCard
                                key={client.clientId}
                                client={client}
                                periodType={periodType}
                                comparisonType={comparisonType}
                                onViewClientProgress={handleViewClientProgress}
                                isOpening={openingClientId === client.clientId}
                            />
                        ))}
                    </div>
                ) : (
                    // ...existing empty state...
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Clients Match</h3>
                                <p className="text-gray-600">
                                    No clients match the selected filters. Try adjusting your filter criteria.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CrossClientDashboardV3;

